import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@gapartners/ui';

import { usePageMetadata } from '@/hooks/usePageMetadata';
import { Header } from '@/components/layout/Header';
import { AmazonConnectPanel } from '@/components/amazon-connect/AmazonConnectPanel';
import { ActiveCallBannerContainer } from '@/components/amazon-connect/ActiveCallBannerContainer';
import {
  CustomerInfoCard,
  LiveTranscriptCard,
  AISuggestionsCard,
  QuickScriptsCard,
  AutoGeneratedCaseCard,
  MissedCallsCard,
  IntentBanner,
  CallHistoryCard,
  NotesCard,
  CallTransferPanel,
  OutboundDialPanel,
  SessionSwitcher,
} from '@/components/contact-center';
import type { QuickScript } from '@/types/quickScripts';
import { useAppSelector } from '@/store/hooks';
import { useAmazonConnectContacts } from '@/hooks/useAmazonConnectContacts';
import { useChatConnectSubscriptions } from '@/hooks/useChatConnectSubscriptions';

export function DashboardPage() {
  const { t } = useTranslation();
  const contactId = useAppSelector(state => state.call.contactId);
  const { activeContacts } = useAmazonConnectContacts();
  const [quickScriptDraft, setQuickScriptDraft] = useState<{
    sequence: number;
    content: string;
  } | null>(null);

  useChatConnectSubscriptions(activeContacts);

  usePageMetadata({
    title: t('pages.dashboard.title'),
    description: t('pages.dashboard.welcome'),
  });

  const handleScriptClick = (script: QuickScript) => {
    if (!contactId) return;
    setQuickScriptDraft(prev => ({
      sequence: (prev?.sequence ?? 0) + 1,
      content: script.content,
    }));
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.50',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
      }}
    >
      <Header />
      <IntentBanner />
      <AmazonConnectPanel />

      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          p: 3,
          pt: 0,
          gap: 2,
          minWidth: 0,
        }}
      >
        <SessionSwitcher />

        {/* Main Layout Grid - on md: 9 cols so right column wraps to next row; on lg: 12 cols 3-6-3 */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(9, 1fr)',
              lg: 'repeat(12, 1fr)',
            },
            gap: 2,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              gridColumn: { md: 'span 3' },
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              minWidth: '16rem',
            }}
          >
            <ActiveCallBannerContainer />
            <CustomerInfoCard />
            <CallTransferPanel />
            <OutboundDialPanel />
            <CallHistoryCard />
          </Box>
          <Box
            sx={{
              gridColumn: { md: 'span 6' },
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              minWidth: '20rem',
            }}
          >
            <LiveTranscriptCard quickScriptDraft={quickScriptDraft} />
            <NotesCard />
            <AISuggestionsCard />
          </Box>
          <Box
            sx={{
              gridColumn: { md: 'span 9', lg: 'span 3' },
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              minWidth: '16rem',
            }}
          >
            <QuickScriptsCard onScriptClick={handleScriptClick} />
            <AutoGeneratedCaseCard />
            <MissedCallsCard />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
